<template>
  <div class="bg-white dark:bg-stone-800 shadow-md rounded-lg overflow-hidden mb-3">
    <div class="p-3">
      <!-- Grid layout: [arrows][content][expander] x [top, toolbar] -->
      <div class="grid grid-cols-[auto,1fr,auto] grid-rows-[auto,auto] gap-x-2 gap-y-1 items-center">
        <!-- Arrows column (vertically centered, consistent height) -->
        <div class="col-start-1 row-start-1 row-span-2 grid place-items-center"
          :class="{ 'opacity-40': !entry.enabled }">
          <div class="flex flex-col items-center gap-1">
            <!-- Up arrow slot -->
            <div class="h-6 w-6">
              <button v-if="i > 0" @click="moveEntry(i, -1)"
                class="h-6 w-6 flex items-center justify-center rounded-md text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 hover:bg-stone-200/50 dark:hover:bg-stone-700/50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                :title="t('common.Move up') || 'Move up'">
                <i class="fas fa-arrow-up text-xs"></i>
              </button>
            </div>
            <!-- Down arrow slot -->
            <div class="h-6 w-6">
              <button v-if="i < entries.length - 1" @click="moveEntry(i, 1)"
                class="h-6 w-6 flex items-center justify-center rounded-md text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 hover:bg-stone-200/50 dark:hover:bg-stone-700/50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                :title="t('common.Move down') || 'Move down'">
                <i class="fas fa-arrow-down text-xs"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Top row: entry input + enable + color + thumbnail -->
        <div class="col-start-2 row-start-1 flex items-center gap-2 min-w-[240px]"
          :class="{ 'opacity-40': !entry.enabled }">
          <!-- Entry text -->
          <input :id="`entry-text-${i}`" ref="textbox" type="text" v-model="entry.text" :disabled="wheelIsBusy"
            :aria-disabled="wheelIsBusy" @input="updateEntryText(i, entry.text)"
            class="w-full px-3 py-1.5 text-sm border border-stone-300 dark:border-stone-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 dark:bg-stone-700 dark:text-stone-200"
            aria-label="Entry text" />

          <!-- Thumbnail (compact, inline) -->
          <div v-if="entry.image" class="relative">
            <img :src="entry.image" :alt="t('advancededitor.Image attached')"
              class="h-6 w-6 rounded object-cover ring-1 ring-stone-200 dark:ring-stone-700" />
            <button @click="removeImageFromEntry(i)"
              class="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-stone-800/80 text-white flex items-center justify-center text-[10px] hover:bg-stone-900/90 focus:outline-none focus:ring-2 focus:ring-primary-500"
              :title="t('winnerdialog.Remove')">
              ×
            </button>
          </div>

          <!-- Enable/disable (keep inline with the name input) -->
          <label :for="`entry-enabled-${i}`" class="flex items-center ml-1">
            <input :id="`entry-enabled-${i}`" type="checkbox" :checked="entry.enabled"
              @change="onToggleEnabled($event.target.checked)" :disabled="wheelIsBusy"
              class="w-4 h-4 text-primary-500 focus:ring-primary-500 border-stone-300 rounded"
              :title="entry.enabled ? (t('advancededitor.Shown on the wheel') || 'Shown on the wheel') : (t('advancededitor.Not shown on the wheel') || 'Not shown on the wheel')" />
          </label>
        </div>

        <!-- Bottom row: toolbar buttons (left-aligned) -->
        <div class="col-start-2 row-start-2 flex items-center gap-1.5" :class="{ 'opacity-40': !entry.enabled }">
          <!-- Color swatch (moved here first in sequence) -->
          <div v-if="wheelConfig.type == 'color'" class="flex items-center">
            <input :id="`entry-color-${i}`" type="color" v-model="entry.color" :disabled="wheelIsBusy"
              :aria-disabled="wheelIsBusy" @change="updateEntryColor(i, entry.color)"
              class="h-6 w-6 rounded border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500"
              :aria-label="t('optionsdialog.Color')" />
          </div>
          <!-- Duplicate -->
          <button @click="duplicateEntry(i)"
            class="p-1 rounded-md text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 hover:bg-stone-200/50 dark:hover:bg-stone-700/50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            :title="t('advancededitor.Duplicate')">
            <i class="far fa-clone"></i>
          </button>

          <!-- Add image -->
          <div class="relative">
            <input type="file" accept="image/*" :disabled="wheelIsBusy" @change="handleImageUpload"
              class="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
            <button @click="attachImageToEntry(i)"
              class="p-1 rounded-md text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 hover:bg-stone-200/50 dark:hover:bg-stone-700/50 focus:outline-none focus:ring-2 focus:ring-primary-500 pointer-events-none"
              :title="t('textboxbuttons.Add image')">
              <i class="far fa-image"></i>
            </button>
          </div>

          <!-- Remove entry -->
          <button @click="removeEntry(i)"
            class="p-1 rounded-md text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 hover:bg-stone-200/50 dark:hover:bg-stone-700/50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            :title="t('winnerdialog.Remove')">
            <i class="fas fa-trash"></i>
          </button>
        </div>

        <!-- Expander column (vertically centered) -->
        <div class="col-start-3 row-start-1 row-span-2 grid place-items-center">
          <button @click="toggleExpanded()" :aria-expanded="expanded" :aria-controls="`adv-panel-${i}`"
            class="p-1 rounded-md text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 hover:bg-stone-200/50 dark:hover:bg-stone-700/50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            :title="expanded ? (t('common.Close') || 'Collapse advanced options') : (t('common.Open') || 'Expand advanced options')">
            <i :class="'fas fa-angle-' + (expanded ? 'up' : 'down')"></i>
          </button>
        </div>
      </div>

      <!-- Collapsible advanced content (unchanged functionality) -->
      <transition name="slide">
        <div v-if="expanded" :id="`adv-panel-${i}`"
          class="mt-2 rounded-md border border-stone-200 dark:border-stone-700 bg-white/60 dark:bg-stone-800/40 p-3 space-y-3">
          <!-- Sound selection -->
          <div class="flex items-center gap-2">
            <i class="fas fa-volume-up text-stone-500 dark:text-stone-400"></i>
            <select v-model="sound" :disabled="wheelIsBusy" :aria-disabled="wheelIsBusy"
              class="w-full px-3 py-2 text-sm border border-stone-300 dark:border-stone-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 dark:bg-stone-700 dark:text-stone-200">
              <option value="">
                {{ t('advancededitor.Inherit from wheel') }}
              </option>
              <option v-for="sound in afterSpinSounds" :value="sound.value" :key="sound.value">
                {{ t(sound.name) }}
              </option>
            </select>
          </div>

          <!-- Message input -->
          <div v-if="wheelConfig.displayWinnerDialog">
            <input type="text" v-model="message" :placeholder="t('advancededitor.Popup message')"
              :disabled="wheelIsBusy" :aria-disabled="wheelIsBusy"
              class="w-full px-3 py-2 text-sm border border-stone-300 dark:border-stone-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 dark:bg-stone-700 dark:text-stone-200" />
          </div>

          <!-- Weight controls + odds -->
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <i class="fas fa-balance-scale-left text-stone-500 dark:text-stone-400"></i>
              <div class="flex items-center border border-stone-300 dark:border-stone-600 rounded-md overflow-hidden">
                <button @click="decrementWeight" :disabled="weight <= 1 || wheelIsBusy"
                  class="px-2 py-1 text-stone-600 hover:text-stone-800 dark:text-stone-300 dark:hover:text-stone-100 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="Decrease weight">
                  <i class="fas fa-minus"></i>
                </button>
                <input :id="`entry-weight-${i}`" type="number" v-model.number="weight" min="1" max="999"
                  :disabled="wheelIsBusy" :aria-disabled="wheelIsBusy"
                  class="w-16 text-center border-0 focus:ring-0 bg-transparent text-stone-800 dark:text-stone-100"
                  aria-label="Weight" />
                <button @click="incrementWeight" :disabled="weight >= 999 || wheelIsBusy"
                  class="px-2 py-1 text-stone-600 hover:text-stone-800 dark:text-stone-300 dark:hover:text-stone-100 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  aria-label="Increase weight">
                  <i class="fas fa-plus"></i>
                </button>
              </div>
            </div>
            <div class="text-xs px-2 py-0.5 rounded bg-stone-100 dark:bg-stone-700 text-stone-700 dark:text-stone-200">
              {{ odds }}%
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useWheelStore } from '@/stores/useWheelStore';
import { useI18n } from 'vue-i18n';
import * as Util from '@utils/Util.js';
import * as Audio from '@services/audio/audio.js';
import * as ImageUtil from '@utils/ImageUtil.js';

const { t } = useI18n();
const wheelStore = useWheelStore();

const props = defineProps({ entry: Object, i: Number });

const expanded = ref(false);
const afterSpinSounds = Audio.getAfterSpinSounds();

const wheelConfig = computed(() => wheelStore.wheelConfig);
const entries = computed(() => wheelConfig.value?.entries || []);
const wheelIsBusy = computed(() => wheelStore.wheelIsBusy);

const sound = computed({
  get() {
    return props.entry.sound || '';
  },
  set(newValue) {
    Util.trackEvent('Wheel', 'SetAdvancedEntrySound', newValue);
    wheelStore.setEntrySound({ index: props.i, sound: newValue });
  }
});

const message = computed({
  get() {
    return props.entry.message || '';
  },
  set(newValue) {
    wheelStore.setEntryMessage({ index: props.i, message: newValue });
  }
});

const weight = computed({
  get() {
    return props.entry.weight;
  },
  set(newValue) {
    wheelStore.setEntryWeight({ index: props.i, weight: newValue });
  }
});

const odds = computed(() => {
  if (!props.entry.enabled) return 0;
  let list = entries.value;
  if (!wheelConfig.value.allowDuplicates) {
    if (Util.entryIsDuplicate(entries.value, props.entry)) return 0;
    list = Util.dedupeEntries(false, entries.value);
  }
  const totalWeight = Util.getTotalWeight(list);
  return +(100 * props.entry.weight / totalWeight).toFixed(1);
});

const textbox = ref(null);
function focus() {
  textbox.value && textbox.value.focus && textbox.value.focus();
}

function toggleExpanded() {
  expanded.value = !expanded.value;
  Util.trackEvent('Wheel', 'ToggleAdvancedExpanded', expanded.value);
}

function updateEntryText(i, text) {
  wheelStore.setEntryText({ index: i, text });
}
function updateEntryColor(i, color) {
  wheelStore.setEntryColor({ index: i, color });
}
function updateEntryWeight(i, w) {
  wheelStore.setEntryWeight({ index: i, weight: w });
}
function incrementWeight() {
  if (weight.value < 999 && !wheelIsBusy.value) {
    wheelStore.setEntryWeight({ index: props.i, weight: weight.value + 1 });
  }
}
function decrementWeight() {
  if (weight.value > 1 && !wheelIsBusy.value) {
    wheelStore.setEntryWeight({ index: props.i, weight: weight.value - 1 });
  }
}
function toggleEntryEnabled() {
  Util.trackEvent('Wheel', 'ToggleEntryEnabled');
  wheelStore.refreshEntries();
}
function onToggleEnabled(enabled) {
  Util.trackEvent('Wheel', 'ToggleEntryEnabled', enabled ? 'enabled' : 'disabled');
  const newEntries = entries.value.slice(0);
  const idx = newEntries.findIndex(e => e.id === props.entry.id);
  if (idx !== -1) {
    newEntries[idx] = { ...newEntries[idx], enabled };
    wheelStore.setEntries(newEntries);
  } else {
    wheelStore.refreshEntries();
  }
}
function removeEntry(index) {
  if (!wheelIsBusy.value) {
    Util.trackEvent('Wheel', 'RemoveAdvancedEntry');
    wheelStore.removeEntryByIndex(index);
  }
}
function moveEntry(i, direction) {
  if (!wheelIsBusy.value) {
    Util.trackEvent('Wheel', 'MoveAdvancedEntry', direction);
    wheelStore.moveEntry({ index: i, direction });
  }
}
function duplicateEntry(i) {
  if (!wheelIsBusy.value) {
    Util.trackEvent('Wheel', 'DuplicateEntry');
    wheelStore.duplicateEntry(i);
  }
}
function attachImageToEntry(i) {
  // No-op, retained for template symmetry (upload handler uses props.i)
}
function removeImageFromEntry(i) {
  if (!wheelIsBusy.value) wheelStore.removeImageFromEntry(i);
}
function handleImageUpload(event) {
  if (!wheelIsBusy.value && event.target.files.length > 0) {
    Util.trackEvent('Wheel', 'AttachImageToEntry');
    const file = event.target.files[0];
    const reader = new FileReader();
    const currentIndex = props.i;
    reader.onload = async function (e) {
      const dataUri = await ImageUtil.optimizeSliceImage(e.target.result);
      await wheelStore.attachImageToEntry({ imageData: dataUri, index: currentIndex });
      setTimeout(() => {
        wheelStore.refreshEntries();
      }, 100);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }
}

defineExpose({ focus });
</script>

<style scoped>
.clickable-icon {
  @apply cursor-pointer;
}

.slide-enter-active {
  transition-duration: 0.3s;
  transition-timing-function: ease-out;
}

.slide-leave-active {
  transition-duration: 0.3s;
  transition-timing-function: ease-out;
}
</style>
